"use client";

import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import { Card, CardContent, Typography, IconButton } from "@mui/material";
import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  CartesianGrid,
} from "recharts";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";


interface FuelGraphProps {
  fuelData?: Array<{
    eD: number;
    rV: number;
    aV: number;
  }>;
}


const FuelGraph: React.FC<FuelGraphProps> = ({ fuelData = [] }) => {
  const theme = useTheme();
  const primaryColor = theme.palette?.primary?.main || "#1976d2";
  const secondaryColor = theme.palette?.secondary?.main || "#9c27b0";

  // Format data for chart with date and time
  const formattedData = fuelData
    .map((entry) => ({
      date: new Date(entry.eD * 1000).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      }),
      time: new Date(entry.eD * 1000).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      rV: entry.rV,
      aV: entry.aV,
    }))
    .sort(
      (a, b) =>
        new Date(a.date).getTime() -
        new Date(b.date).getTime()
    );

  // Get unique dates in data
  const uniqueDates = Array.from(new Set(formattedData.map((d) => d.date)));

  const windowSize = 6;
  const [startDateIndex, setStartDateIndex] = useState(0);

  const handleBack = () => {
    if (startDateIndex > 0) setStartDateIndex(startDateIndex - 1);
  };

  const handleForward = () => {
    if (startDateIndex + windowSize < uniqueDates.length)
      setStartDateIndex(startDateIndex + 1);
  };

  // Compute window dates
  const windowDates = uniqueDates.slice(
    startDateIndex,
    startDateIndex + windowSize
  );

  // Filter data to show only in window dates
  const windowData = formattedData.filter((d) =>
    windowDates.includes(d.date)
  );

  // Custom tick formatter to show date only once when it changes
  const customTickFormatter = (() => {
    let lastDate = "";
    return (tick: string) => {
      if (tick !== lastDate) {
        lastDate = tick;
        return tick;
      }
      return "";
    };
  })();

  return (
    <Card className="w-full h-full flex flex-col items-start">
      <div className="flex justify-between w-full items-center px-4 pt-4">
        <Typography variant="button" color="secondary" fontWeight={400}>
          Fuel Level Graph
        </Typography>
        <div>
          <IconButton onClick={handleBack} disabled={startDateIndex === 0}>
            <ArrowBackIosIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={handleForward}
            disabled={startDateIndex + windowSize >= uniqueDates.length}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
      <CardContent className="border rounded-xl border-[#F0EFF8] w-full">
        <div className="flex gap-1 pt-4">
          <div className="relative" style={{ left: "8px", top: "105px" }}>
            <Typography
              variant="caption"
              color="textPrimary"
              sx={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                textAlign: "center",
                mr: -1,
              }}
            >
              Fuel (Litres)
            </Typography>
          </div>
          <div className="flex-1 min-h-[200px] max-h-[400px]">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={windowData}>
                <defs>
                  <linearGradient id="fuelGradient" x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0%" stopColor={`${primaryColor}00`} />
                    <stop offset="25%" stopColor={`${primaryColor}40`} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tickFormatter={customTickFormatter} />
                <YAxis domain={[0, "dataMax"]} />
                <CartesianGrid strokeDasharray="3 3" />
                <Tooltip
                  cursor={false}
                  contentStyle={{
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                  labelFormatter={(label, payload) => {
                    if (payload && payload.length > 0) {
                      return `Date: ${payload[0].payload.date} Time: ${payload[0].payload.time}`;
                    }
                    return label;
                  }}
                  formatter={(value: number, name: string) => {
                    if (name === "rV") {
                      return [`${value} L`, "Raw Value"];
                    } else if (name === "aV") {
                      return [`${value} L`, "Average Value"];
                    }
                    return [`${value} L`, name];
                  }}
                />
                <Area
                  type="monotoneX"
                  dataKey="rV"
                  stroke={primaryColor}
                  strokeWidth={2}
                  fill="url(#fuelGradient)"
                  fillOpacity={1}
                  name="Raw Value"
                />
                <Area
                  type="monotoneX"
                  dataKey="aV"
                  stroke={secondaryColor}
                  strokeWidth={2}
                  fillOpacity={0}
                  name="Average Value"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        {fuelData.length === 0 && (
          <div className="w-full flex justify-center">
            <Typography
              variant="caption"
              color="textPrimary"
              align="center"
              className="mt-2 w-full"
            >
              No Fuel Data
            </Typography>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FuelGraph;
